import { memo, useCallback } from 'react'

import { format, isThisYear, isToday, parse } from 'date-fns'
import { isEqual } from 'lodash'
import _ from 'lodash/fp'

import { Separator } from '@/components/ui/separator.tsx'
import type { Message } from '@/modules/chatting/types.ts'

import { ChatMessage } from './ChatMessage.tsx'

interface Props {
  date: string
  messages: Message[]
  highlightMessage: (messageId: number) => void
  searchedKeyword?: string
  searchedMessageId: Message['id'] | null
  onClickReply?: () => void
}

const ChatMessageGroupComponent = ({
  date,
  messages,
  highlightMessage,
  searchedKeyword,
  searchedMessageId,
  onClickReply,
}: Props) => {
  const sentDate = parse(date, 'dd MM yyyy', new Date())
  const hasMessage = useCallback(
    (messageId: number) => messages.some((m) => m.id === messageId),
    [messages],
  )

  return (
    <div className="relative my-4">
      <div className="flex justify-center sticky top-2 z-[1] mb-4 pointer-events-none">
        <span className="bg-background text-secondary-300 px-2 text-sm rounded-full">
          {isToday(sentDate)
            ? 'Today'
            : isThisYear(sentDate)
              ? format(sentDate, 'MMM dd')
              : format(sentDate, 'MMM dd, yyyy')}
        </span>
      </div>
      <Separator className="absolute top-[10px]" />
      {_.uniqBy('id', messages)?.map((m, i, self) => (
        <ChatMessage
          searchedKeyword={
            searchedMessageId === m.id ? searchedKeyword : undefined
          }
          highlightMessage={highlightMessage}
          message={m}
          key={m.id}
          prevMessage={i > 0 ? self[i - 1] : undefined}
          nextMessage={i < self.length - 1 ? self[i + 1] : undefined}
          hasMessage={hasMessage}
          onClickReply={onClickReply}
        />
      ))}
    </div>
  )
}

export const ChatMessageGroup = memo(
  ChatMessageGroupComponent,
  (prev, next) => {
    if (
      prev.searchedKeyword !== next.searchedKeyword ||
      prev.searchedMessageId !== next.searchedMessageId
    ) {
      return false
    }

    if (prev.date === next.date) {
      return isEqual(prev.messages, next.messages)
    }

    return (
      prev.date === next.date || prev.highlightMessage === next.highlightMessage
    )
  },
)
